import { IGwGFolder } from '../../../Interfaces/IGwGFolder';
import { HealthCheckParams, IHealthCheckInput } from '../types';

/**
 * Regeln zum Bereich „GwG" (#1283).
 *
 * `GwGFolderArray` ist eine vom Backend bei jedem Laden neu aufgebaute Sicht auf
 * die GwG-Unterlagen der beteiligten juristischen Personen. Fehlt sie (kein
 * GwG-Ordner im Vorgang), liefern die Regeln keinen Fund.
 */

const folders = ({ legalTransaction }: IHealthCheckInput): IGwGFolder[] =>
  legalTransaction.GwGFolderArray ?? [];

/**
 * Pflichtunterlagen fehlen. Bisher nur ein Statuschip über der Ansicht, obwohl
 * es fachlich der gewichtigste Fund des ganzen Katalogs ist.
 */
export const mandatoryDocumentsMissing = (input: IHealthCheckInput) =>
  folders(input)
    .filter(folder => folder.Completeness.missing > 0)
    .map(folder => {
      const { mandatoryFulfilled, mandatoryTotal, missing } =
        folder.Completeness;

      return `GwG-Ordner: ${mandatoryFulfilled} von ${mandatoryTotal} Pflichtunterlagen vorhanden — ${
        missing === 1
          ? '1 Pflichtunterlage fehlt'
          : `${missing} Pflichtunterlagen fehlen`
      }.`;
    });

/**
 * Für den GwG-Ordner ist noch keine Dokumentart als Pflicht festgelegt. Der
 * Ordner existiert dann, überwacht aber nichts.
 */
export const noMandatoryDocumentTypes = (input: IHealthCheckInput) =>
  folders(input)
    .filter(
      folder =>
        folder.DocumentTypeArray.length === 0 ||
        folder.Completeness.mandatoryTotal === 0
    )
    .map(
      () =>
        'Für den GwG-Ordner ist noch keine Pflichtunterlage festgelegt. Der Ordner überwacht damit nichts.'
    );

/**
 * Eine GwG-Unterlage ist abgelaufen oder läuft in den nächsten Tagen ab.
 *
 * `ValidUntil` wird am Dokument geführt, fließt aber nicht in die
 * Vollständigkeitsberechnung ein — ein abgelaufener Nachweis gilt dort weiterhin
 * als vorhanden. Parameter `leadDays` steuert die Vorwarnzeit.
 */
export const documentExpiring = (
  input: IHealthCheckInput,
  params: HealthCheckParams
) => {
  const leadDays = Number(params.leadDays ?? 30);
  const limit = new Date();
  limit.setHours(0, 0, 0, 0);
  limit.setDate(limit.getDate() + leadDays);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const findings: string[] = [];

  folders(input).forEach(folder => {
    folder.DocumentTypeArray.forEach(documentType => {
      documentType.ParticipantArray.forEach(participant => {
        const validUntil = participant.CurrentVersion?.ValidUntil;

        if (!validUntil) {
          return;
        }

        const validUntilDate = new Date(validUntil);

        if (Number.isNaN(validUntilDate.getTime()) || validUntilDate > limit) {
          return;
        }

        const formatted = validUntilDate.toLocaleDateString('de-DE');
        const label = `${documentType.LegalClientDocumentType} von ${participant.LegalClient}`;

        findings.push(
          validUntilDate < today
            ? `GwG-Unterlage „${label}" ist am ${formatted} abgelaufen.`
            : `GwG-Unterlage „${label}" läuft am ${formatted} ab.`
        );
      });
    });
  });

  return findings;
};

/**
 * Der GwG-Ordner ist empfohlen, aber noch nicht angelegt. Reiner Hinweis, daher
 * `info` — er zählt nicht als Warnung.
 *
 * Das Kriterium ist allein die Beteiligung einer juristischen Person. Ein
 * Grundstück war zunächst zusätzlich verlangt; das war fachlich falsch, weil die
 * geldwäscherechtliche Prüfung an der Beteiligung und nicht am Kaufgegenstand
 * hängt — gerade bei gesellschaftsrechtlichen Vorgängen schlug die Empfehlung
 * dadurch nie an (QS #23). Die Bedingung wertet `shouldSuggestGwGFolder` im
 * Backend aus; sie schließt einen bereits vorhandenen Ordner und eine
 * abgelehnte Empfehlung mit ein.
 */
export const folderSuggested = ({ legalTransaction }: IHealthCheckInput) =>
  legalTransaction.suggestGwGFolder === true &&
  !legalTransaction.GwGSuggestionDismissed_at
    ? [
        'Für diesen Vorgang wird ein GwG-Ordner empfohlen: es ist eine juristische Person beteiligt.',
      ]
    : [];
